import type { Article } from "~/types/article"
import type { NavListChapter } from "~/types/nav"
import type { Subject } from "~/types/subject"
import {
  useQueries,
  useQueryClient,
} from "@tanstack/vue-query"
import { useRoute as useNativeRoute } from "vue-router"
import { formatData } from "~/mapping/article/format-data"
import { ContentType } from "~/models/Content/ContentType"
import {
  formatRestrictedArticles,
  formatSubjectPage,
} from "~/utilities/articles"
import { generateArticlePath } from "~/utilities/urls"

export const useProductContentStore = defineStore(
  "productContent",
  () => {
    /** State */
    // FIXME: We should be able to use the auto-imported useRoute from Nuxt here, but for some reason reactivity breaks. Looks related to this issue: https://github.com/nuxt/nuxt/issues/28804
    const route = useNativeRoute()

    const productStore = useProductVariantsStore()

    const subjectsAndFolders = useSubjectsAndFolders(computed(() => productStore.activeVariant?.locationId))

    const router = useRouter()

    const subjects = computed(() => subjectsAndFolders.data.value?.subjects?.map(subject => ({
      ...subject,
      frontendHref: router.resolve({
        name: "subject",
        params: {
          product: productStore.activeProduct?.productKey ?? "",
          variant: productStore.activeVariant?.variantKey ?? "",
          subject: sanitizeForUrl(subject.title),
        },
        query: {
          kapittel: subject.id,
        },
      }),
      restricted:
        !subject.isFree &&
        !productStore.activeVariant?.userHasLicence &&
        !productStore.activeVariant?.isFree,
      // Show free label if the content is free and user doesn't have a license
      showFreeLabel: subject.isFree && !productStore.activeVariant?.userHasLicence,
    })) ?? [])

    const folders = computed(() => subjectsAndFolders.data.value?.folders ?? [])

    const activeSubject = computed(() => {
      const subjectId = Number(route.query.kapittel)
      return subjectId ? subjects.value.find(s => s.id === subjectId) : undefined
    })

    function isValidSubject(subject: Subject | undefined): subject is Subject {
      return !!subject && !subject.restricted && !!subject.totalChildren
    }

    const nextSubject = computed(
      () => findAdjacent(subjects.value, activeSubject.value, "next", isValidSubject),
    )

    const previousSubject = computed(
      () => findAdjacent(subjects.value, activeSubject.value, "previous", isValidSubject),
    )

    /** Getters */
    const subjectNav = computed(() =>
      subjects.value
        .map<NavListChapter>((i) => {
          return {
            id: i.id,
            parentLocationId: i.parentLocationId,
            text: i.title,
            frontendHref: i.frontendHref,
            priority: i.priority,
            hidden: i.hidden,
            restricted: i.restricted || false,
            showFreeLabel: i.showFreeLabel || false,
            type: i.type,
            totalChildren: i.totalChildren,
            hasIntroPages: i.introPages.length > 0,
          }
        })
        .sort(byPriority),
    )

    const queryClient = useQueryClient()

    const contentQueryKey = ["content"] as const

    async function invalidateContent() {
      await queryClient.invalidateQueries({
        queryKey: contentQueryKey,
      })
    }

    const { $parseStringToDocument } = useNuxtApp()

    const getContent = useGetContent()

    /** Fetch articles */
    const articlesQuery = useQueries({
      queries: [previousSubject, activeSubject, nextSubject].map(
        sub => ({
          queryKey: [
            ...contentQueryKey,
            "articles",
            computed(() => sub.value?.id),
            computed(() => sub.value?.restricted),
          ],
          enabled: computed(() => !!sub.value),
          staleTime: 1000 * 60 * 60 * 6, // 6 hours
          async queryFn() {
            const s = sub.value

            if (!s) {
              return []
            }

            let articles = await getContent({
              childDepth: 3,
              contentType: [ContentType.Article],
              parentLocationId: [s.id],
              sortField: "priority",
              sortOrder: "asc",
            })

            articles = articles.map(a => formatData(a, $parseStringToDocument))

            // if the subject is restricted, we need to format the articles in restricted format
            if (s.restricted) {
              articles = formatRestrictedArticles(articles, s.id)
            } else {
              const subjectPage = formatSubjectPage(s)
              if (subjectPage)
                articles.unshift(subjectPage)
            }

            return articles.map((a) => {
              if (!a.frontendHref) {
                a.frontendHref = {
                  path: generateArticlePath(
                    productStore.activeProduct?.productKey,
                    productStore.activeVariant?.variantKey,
                    s.title,
                    a.title,
                  ),
                  query: {
                    artikkel: a.id,
                    kapittel: a.parentLocationId ?? 0,
                  },
                }
              }

              return a
            })
          },
        }),
      ),
    })

    const allArticles = computed(() => {
      const all = articlesQuery.value.flatMap(it => it.data ?? [])

      const sortedArticles: Article[] = []

      for (const subject of subjects.value) {
        const articles = all
          .filter(article => article?.parentLocationId === subject.id)
          .sort((a, b) => {
            // Intro slides should always be the first article in a product
            if (
              a.template === "USlidesArticle" &&
              b.template !== "USlidesArticle"
            ) {
              return -1
            }
            if (
              a.template !== "USlidesArticle" &&
              b.template === "USlidesArticle"
            ) {
              return 1
            }

            return byPriority(a, b)
          })

        sortedArticles.push(...articles)
      }

      return sortedArticles
    })

    const articles = computed<Article[]>(() =>
      allArticles.value.filter(
        a => a.parentLocationId === activeSubject.value?.id,
      ),
    )

    const articleNav = computed(() =>
      articles.value.map<NavListChapter>((i) => {
        return {
          id: i.id,
          parentLocationId: i.parentLocationId,
          text: i.title,
          frontendHref: i.frontendHref,
          priority: i.priority,
          hidden: i.hidden ?? false,
          restricted: i.restricted || false,
          hasIntroPages: false,
        }
      }),
    )

    const activeArticle = computed(() => {
      const articleId = Number(route.query.artikkel)
      return articleId ? articles.value.find(a => a.id === articleId) : undefined
    })

    return {
      subjects,
      articles,
      activeArticle,
      nextArticle: computed(
        () => findAdjacent(allArticles.value, activeArticle.value, "next"),
      ),
      previousArticle: computed(
        () => findAdjacent(allArticles.value, activeArticle.value, "previous"),
      ),
      folders,
      subjectNav,
      articleNav,
      activeSubject,
      nextSubject,
      previousSubject,
      noSubjectsFound: computed(
        () => !activeSubject.value && subjectsAndFolders.isSuccess.value,
      ),
      noArticlesFound: computed(
        () =>
          !activeArticle.value &&
          articlesQuery.value.every(it => it.isSuccess),
      ),
      allArticles,
      hydrationInProgress: computed(
        () =>
          subjectsAndFolders.isFetching.value ||
          articlesQuery.value.some(it => it.isFetching),
      ),
      invalidateContent,
    }
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(
    acceptHMRUpdate(useProductContentStore, import.meta.hot),
  )
}
